import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, startAfter, where, DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

const PAGE_SIZE = 10;

export const getTodayDateKey = () => {
  // Utilise la date locale du navigateur en format YYYY-MM-DD
  return new Date().toLocaleDateString('fr-CA'); // fr-CA donne YYYY-MM-DD
};

export interface EvangileItem {
  id: string;
  date: string;
  reference: string;
  accroche: string;
  contenu: string;
  caption: string | null;
  imageUrl: string;
  thumbUrl: string;
  imagePortraitUrl: string | null;
  portraitThumbUrl: string | null;
  videoUrl: string | null;
  views: number;
}

export const normalizeEvangile = (doc: QueryDocumentSnapshot<DocumentData>): EvangileItem => {
  const data = doc.data();
  const dateValue = data.date || doc.id;
  return {
    id:               doc.id,
    date:             dateValue,
    reference:        data.reference || '',
    accroche:         data.accroche || '',
    contenu:          data.contenu || '',
    caption:          data.caption || null,
    imageUrl:         data.image_url || '',
    thumbUrl:         data.image_thumb_url || data.image_url || '',
    imagePortraitUrl: data.image_portrait_url || null,
    portraitThumbUrl: data.image_portrait_thumb_url || null,
    videoUrl:         data.video_url || null,
    views:            typeof data.views === 'number' ? data.views : 0,
  };
};

export const useEvangile = (lang: string) => {
  const [items, setItems] = useState<EvangileItem[]>([]);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [hasMore, setHasMore] = useState(true);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHasMore(true);
    try {
      const todayKey = getTodayDateKey();
      const q = query(
        collection(db, `evangile_${lang}`),
        where('date', '<=', todayKey),
        orderBy('date', 'desc'),
        limit(PAGE_SIZE)
      );
      const snapshot = await getDocs(q);
      const allDocs = snapshot.docs.map(doc => normalizeEvangile(doc));
      // Filtre côté client en sécurité (au cas où le cache Firestore ignore le where)
      const docs = allDocs.filter(d => d.date <= todayKey);
      setItems(docs);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('[useEvangile] loadInitial error:', err);
      setError(err);
      setItems([]);
      setLastDoc(null);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [lang]);

  const loadMore = useCallback(async () => {
    if (!lastDoc || loadingMore || !hasMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const todayKey = getTodayDateKey();
      const q = query(
        collection(db, `evangile_${lang}`),
        where('date', '<=', todayKey),
        orderBy('date', 'desc'),
        startAfter(lastDoc),
        limit(PAGE_SIZE)
      );
      const snapshot = await getDocs(q);
      const allDocs = snapshot.docs.map(doc => normalizeEvangile(doc));
      const docs = allDocs.filter(d => d.date <= todayKey);
      setItems(prev => [...prev, ...docs]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1] || lastDoc);
      setHasMore(snapshot.docs.length === PAGE_SIZE);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, lang, lastDoc, loadingMore]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  return {
    items,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore
  };
};
